import { NavigateFunction } from "react-router-dom";
import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Coûts LLM — client de l'API backoffice `/admin/llm-costs/*`.
 *
 * ⚠️ Tous les montants sont des ESTIMATIONS calculées par back/ (tokens × grille
 * tarifaire locale). La passerelle LiteLLM détient la grille du fournisseur et
 * fait foi sur la facture. L'écart entre les deux est le signal à surveiller ;
 * `unpriced_calls` en est la cause la plus fréquente.
 */

export interface CostBucket {
  key: string;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  estimated_cost_usd: number;
  /**
   * Appels de ce bucket dont le modèle n'a pas de tarif connu. Sans ce compteur,
   * un bucket entièrement non tarifé affiche « $0.00 », ce qui se lit « gratuit »
   * au lieu de « non mesuré ».
   */
  unpriced_calls: number;
}

export interface LlmCostSummary {
  window_days: number;
  since: string;
  totals: {
    calls: number;
    prompt_tokens: number;
    completion_tokens: number;
    estimated_cost_usd: number;
    errors: number;
    /** Appels réussis dont le modèle est absent de la grille : angle mort du coût. */
    unpriced_calls: number;
  };
  by_operation: CostBucket[];
  by_model: CostBucket[];
  cost_basis: string;
  billing_authority: string;
}

export interface OrganizationCostRow {
  organization_id: number | null;
  organization_name: string;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  estimated_cost_usd: number;
}

export interface TimeseriesPoint {
  day: string;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  estimated_cost_usd: number;
}

export interface TopCallRow {
  id: number;
  created_at: string | null;
  operation: string;
  organization_id: number | null;
  alias: string;
  model: string;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  estimated_cost_usd: number;
  duration_seconds: number | null;
  status: string;
}

export async function getLlmCostSummary(
  navigate: NavigateFunction,
  days: number
): Promise<LlmCostSummary> {
  const url = `${API_URL}/admin/llm-costs/summary?days=${days}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des coûts LLM");
  return res.json();
}

export async function getLlmCostByOrganization(
  navigate: NavigateFunction,
  days: number
): Promise<{ window_days: number; results: OrganizationCostRow[] }> {
  const url = `${API_URL}/admin/llm-costs/by-organization?days=${days}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des coûts par organisation");
  return res.json();
}

export async function getLlmCostTimeseries(
  navigate: NavigateFunction,
  days: number,
  operation?: string
): Promise<{ window_days: number; operation: string | null; points: TimeseriesPoint[] }> {
  const q = new URLSearchParams({ days: String(days) });
  if (operation) q.set("operation", operation);
  const url = `${API_URL}/admin/llm-costs/timeseries?${q}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement de la série temporelle");
  return res.json();
}

export async function getLlmTopCalls(
  navigate: NavigateFunction,
  days: number
): Promise<{ window_days: number; results: TopCallRow[] }> {
  const url = `${API_URL}/admin/llm-costs/top-calls?days=${days}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des appels les plus coûteux");
  return res.json();
}

/** Formate un montant USD avec assez de décimales pour rester lisible en centimes. */
export function formatUsd(value: number): string {
  if (value === 0) return "$0";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

export function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)} k`;
  return String(value);
}
